import { useEffect, useState } from "react";
import axiosClient from "../api/axiosClient";
import "../styles/Adaptive.css";
import Editor from "@monaco-editor/react";
import { useLocation, useNavigate } from "react-router-dom";

const DIFF_META = {
  easy:   { label: "Easy",   icon: "🌱", progressColor: "#16a34a" },
  medium: { label: "Medium", icon: "🔥", progressColor: "#d97706" },
  hard:   { label: "Hard",   icon: "⚡", progressColor: "#dc2626" },
};

export default function Adaptive() {
  const navigate = useNavigate();
  const location = useLocation();

  // ───────────────── CLEAR STALE SESSION ON FRESH NAVIGATION ─────────────────
  useEffect(() => {
    if (location.state?.topic) {
      localStorage.removeItem("selectedTopic");
    }
  }, []);

  const selectedTopic =
    location.state?.topic || localStorage.getItem("selectedTopic");

  // ───────────────── STATE ─────────────────
  const [questions, setQuestions]       = useState([]);
  const [current, setCurrent]           = useState(0);
  const [loading, setLoading]           = useState(true);
  const [popupLoading, setPopupLoading] = useState(false);
  const [error, setError]               = useState("");
  const [language, setLanguage]         = useState("javascript");
  const [timeElapsed, setTimeElapsed]   = useState(0);
  const [code, setCode]                 = useState("");
  const [isSubmitted, setIsSubmitted]   = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [testName, setTestName]         = useState("");
  const [showLimitPopup, setShowLimitPopup] = useState(false);
  const [limitMessage, setLimitMessage] = useState(null);

  // ───────────────── TIMER ─────────────────
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeElapsed((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // ───────────────── BACK BUTTON / TAB CLOSE ─────────────────
  useEffect(() => {
    const handleBeforeUnload = () => {
      const resolvedTestName = localStorage.getItem("testName");
      const token = localStorage.getItem("token");
      if (!resolvedTestName) return;
      fetch(
        `https://codematrix.co.in/auth/adaptiveFinishTest?testName=${encodeURIComponent(resolvedTestName)}`,
        { method: "POST", keepalive: true, headers: { Authorization: token } }
      );
    };

    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      const resolvedTestName = localStorage.getItem("testName");
      const token = localStorage.getItem("token");
      if (!resolvedTestName) {
        navigate("/front");
        return;
      }
      axiosClient
        .post("/auth/adaptiveFinishTest", null, {
          params: { testName: resolvedTestName },
          headers: { Authorization: token },
        })
        .finally(() => navigate("/front"));
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // ───────────────── HELPERS ─────────────────
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const handleLimitMessage = (msg, data, isMilestone = false) => {
    setLimitMessage({
      title: isMilestone ? "🏆 Milestone Reached!" : "Test Message",
        description: isMilestone ? null : msg,  // ← null for milestone
      footer: isMilestone ? "" : "You can try again later",
      data: data || null,
      isMilestone,
    });
    setShowLimitPopup(true);
  };

  // ───────────────── FINISH TEST ─────────────────
  // ✅ defined BEFORE any early returns
  const handleFinishTest = async () => {
    try {
      const token = localStorage.getItem("token");
      const resolvedTestName = testName || localStorage.getItem("testName");
      if (!resolvedTestName) {
        navigate("/front");
        return;
      }
      await axiosClient.post("/auth/adaptiveFinishTest", null, {
        params: { testName: resolvedTestName },
        headers: { Authorization: token },
      });
    } catch (err) {
      console.log(err);
    } finally {
      navigate("/front");
    }
  };

  // ───────────────── NEXT QUESTION ─────────────────
  // ✅ defined BEFORE any early returns
  const handleNextQuestion = async (fromPopup = false) => {
    try {
      if (!selectedTopic) return;

      if (fromPopup) {
        setPopupLoading(true);
      } else {
        setLoading(true);
      }

      const token = localStorage.getItem("token");
      const encodedTopic = encodeURIComponent(selectedTopic);

      const res = await axiosClient.get(`/auth/adaptiveQuestion/${encodedTopic}`, {
        headers: { Authorization: token },
      });

      const data = res.data;
      console.log("NEXT QUESTION RESPONSE =>", data);

      if (data?.success === false && data?.message) {
        setQuestions([]);
        setShowLimitPopup(false);
        handleLimitMessage(data.message, data, false);
        return;
      }

      setCurrent(0);
      setQuestions([data]);
      localStorage.setItem("testName", data.testName);
      setTestName(data.testName);
      setCode("");
      setIsSubmitted(false);
      setTimeElapsed(0);
      setShowLimitPopup(false);
      setLimitMessage(null);

    } catch (err) {
      console.log("FETCH ERROR =>", err.response?.data || err.message);
      const msg = err.response?.data?.message || "Something went wrong";
      setQuestions([]);
      setShowLimitPopup(false);
      handleLimitMessage(msg, err.response?.data);
    } finally {
      setLoading(false);
      setPopupLoading(false);
    }
  };

  // ───────────────── FETCH QUESTIONS ─────────────────
  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      if (!selectedTopic) return;

      localStorage.setItem("selectedTopic", selectedTopic);
      setLoading(true);
      setError("");

      const token = localStorage.getItem("token");
      const encodedTopic = encodeURIComponent(selectedTopic);

      const res = await axiosClient.get(`/auth/adaptiveQuestion/${encodedTopic}`, {
        headers: { Authorization: token },
      });

      const data = res.data;
      console.log("QUESTION RESPONSE =>", data);

      if (data?.success === false && data?.message) {
        handleLimitMessage(data.message, data, false);
        setQuestions([]);
        return;
      }

      setTestName(data.testName);
      localStorage.setItem("testName", data.testName);
      setQuestions([data]);

    } catch (err) {
      const msg = err.response?.data?.message;
      if (err.response?.data?.success === false) {
        handleLimitMessage(msg, err.response.data, false);
        setQuestions([]);
        setTestName(err.response?.data?.testName || "");
        return;
      }
      setError("Failed to load question");
    } finally {
      setLoading(false);
    }
  };

  // ───────────────── SUBMIT ─────────────────
  // ✅ defined BEFORE early returns so it's always available
  const submitque = async () => {
    try {
      if (!code.trim()) {
        alert("Please write code first");
        return;
      }

      setSubmitLoading(true);
      setSubmitResult(null);

      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      const q = questions[current];

      const payload = {
        userId,
        uniqueTestName: q.testName,
        currentQuestionId: q.id,
        mainQuestionId: q.mainQuestionId,
        difficultyLevel: q.difficultyLevel,
        language,
        timeTakenSeconds: timeElapsed,
        userAnswer: code,
      };

      const res = await axiosClient.post("/auth/adaptiveSaveAnswer", payload, {
        headers: { Authorization: token, "Content-Type": "application/json" },
      });

      console.log("SUCCESS =>", res.data);

      if (res.data?.data?.triggerMilestone) {
        setSubmitLoading(false);
        handleLimitMessage(res.data.data.milestoneMessage, res.data.data, true);
      } else {
        setSubmitResult({ success: true, message: res.data?.message || "Answer Submitted 🎉" });
        setSubmitLoading(false);
        setIsSubmitted(true);
      }

    } catch (error) {
      console.log(error);
      setSubmitLoading(false);
      const errorMessage = error?.response?.data?.message || "Wrong Answer ❌";
      setSubmitResult({ success: false, message: errorMessage });

      if (error?.response?.data?.success === false && errorMessage.includes("Test locked")) {
        await handleFinishTest();
      }
    }
  };

  // ───────────────── EARLY RETURNS ─────────────────
  // ✅ all functions defined above, so no "used before init" errors

  if (loading) {
    return <div className="qq-loading">Loading questions...</div>;
  }

  // ✅ popup check BEFORE empty check
//   if (showLimitPopup) {
//     return (
//       <div className="limit-overlay">
//         <div className="limit-card warning">
//           <h2>{limitMessage?.title || ""}</h2>
//          {/* ✅ only show description for non-milestone */}
// {!limitMessage?.isMilestone && (
//   <p>{limitMessage?.description || ""}</p>
// )}

//           {limitMessage?.isMilestone && limitMessage?.data?.milestoneMessage && (
//             <p>{limitMessage.data.milestoneMessage}</p>
//           )}

//           {!limitMessage?.isMilestone && limitMessage?.footer && (
//             <p>{limitMessage.footer}</p>
//           )}

//          <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "20px" }}>
//             <button className="gotitbtn" onClick={handleFinishTest}>
//               {limitMessage?.isMilestone ? "Finish Here" : "Got it"}
//             </button>

//             {limitMessage?.isMilestone && (
//               <button
//                 className="gotitbtn"
//                 disabled={popupLoading}
//                 onClick={() => handleNextQuestion(true)}
//               >
//                 {popupLoading ? "Loading..." : "Continue Challenge"}
//               </button>
//             )}
//           </div>
//         </div>
//       </div>
//     );
//   }

  if (!questions.length  && !showLimitPopup) {
    return <div className="qq-empty">Failed to load question</div>;
  }

const q = questions[current] || {};
const total = questions.length;
const meta = DIFF_META[q.difficultyLevel?.toLowerCase()] || DIFF_META["easy"];
const diff = q.difficultyLevel?.toLowerCase() || "easy";

  // ───────────────── RENDER ─────────────────
  return (
    <div className={`qq-page qq-page--${diff}`}>
      <div className="qq-card">

        {/* TOP BAR */}
        <div className={`qq-topbar qq-topbar--${diff}`}>
          <div className={`qq-pill qq-pill--${diff}`}>
            <span>{meta.icon}</span>
            {meta.label}
          </div>
          <div className="qq-progress-wrap">
            <div className="qq-progress-track">
              <div
                className="qq-progress-fill"
                style={{
                  width: `${((current + 1) / total) * 100}%`,
                  background: meta.progressColor,
                }}
              />
            </div>
          </div>
          <span className={`qq-count qq-count--${diff}`}>
            {current + 1} / {total}
          </span>
        </div>

        {/* META */}
        <div className={`qq-meta qq-meta--${diff}`}>
          {q.topic && (
            <span className={`qq-tag qq-tag--module qq-tag--${diff}`}>
              {q.topic}
            </span>
          )}
          <span className={`qq-tag qq-tag--role qq-tag--${diff}`}>
            {q.difficultyLevel}
          </span>
        </div>

        {/* BODY */}
        <div className="qq-body">

          {/* TIMER */}
          <div className={`qq-timer qq-timer--${diff}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12,6 12,12 16,14" />
            </svg>
            <span>{formatTime(timeElapsed)}</span>
          </div>

          {/* TITLE */}
          <h2 className={`qq-qtitle qq-qtitle--${diff}`}>
            {q.topic}
          </h2>

          {/* QUESTION */}
          <p className={`qq-para qq-para--${diff}`}>
            {q.question}
          </p>

          {/* EXAMPLES */}
          {q.examplesJson && q.examplesJson.length > 0 && (
            <div className="qq-example-wrap">
              <h3 className={`qq-section-title qq-section-title--${diff}`}>
                Examples
              </h3>
              {q.examplesJson.map((example, index) => (
                <div key={index} className={`qq-example qq-example--${diff}`}>
                  <div className="qq-example-block">
                    <span className="qq-label">Input:</span>
                    <pre className={`qq-code qq-code--${diff}`}>
                      {JSON.stringify(example.input, null, 2)}
                    </pre>
                  </div>
                  <div className="qq-example-block">
                    <span className="qq-label">Output:</span>
                    <pre className={`qq-code qq-code--${diff}`}>
                      {JSON.stringify(example.output, null, 2)}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CONSTRAINTS */}
          {q.constraintsJson && q.constraintsJson.length > 0 && (
            <div className="qq-constraints">
              <h3 className={`qq-section-title qq-section-title--${diff}`}>
                Constraints
              </h3>
              <ul className="qq-constraints-list">
                {q.constraintsJson.map((constraint, index) => (
                  <li key={index} className={`qq-constraint qq-constraint--${diff}`}>
                    {constraint}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* EDITOR */}
        <div className="qq-editor-section">
          <div className="qq-editor-top">
            <h3 className={`qq-section-title qq-section-title--${diff}`}>
              Code Editor
            </h3>
            <select
              className="qq-language-select"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="javascript">JavaScript</option>
              <option value="java">Java</option>
              <option value="python">Python</option>
              <option value="cpp">C++</option>
            </select>
          </div>

          <div className="qq-editor-wrapper">
            <Editor
              height="500px"
              theme="vs-dark"
              language={language}
              value={code}
              onChange={(value) => setCode(value || "")}
              options={{
                fontSize: 15,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                automaticLayout: true,
                wordWrap: "on",
                padding: { top: 16 },
              }}
            />
          </div>

          {/* ACTIONS */}
          <div className="qq-actions">
            <button onClick={handleFinishTest} className="qq-btn qq-btn--finish">
              Finish Test
            </button>
            <button
              onClick={submitque}
              className={`qq-btn qq-btn--submit qq-btn--${diff}`}
            >
              Submit Code
            </button>
          </div>
        </div>
      </div>

      {/* SUBMIT POPUP */}
      {(submitLoading || submitResult) && (
        <div className="qq-popup-overlay">
          <div className="qq-popup">
            {submitLoading ? (
              <>
                <div className="qq-loader"></div>
                <h2 className="qq-popup-title">Evaluating Your Code</h2>
                <p className="qq-popup-text">Please wait while we check your solution...</p>
              </>
            ) : (
              <>
                <div className="qq-popup-icon">
                  {submitResult.success ? "✅" : "❌"}
                </div>
                <h2 className="qq-popup-title">
                  {submitResult.success ? "Answer Submitted" : "Wrong Answer"}
                </h2>
                <p className="qq-popup-text">{submitResult.message}</p>
                <button
                  className="qq-popup-btn"
                  onClick={() => {
                    setSubmitResult(null);
                    if (submitResult?.success) {
                      handleNextQuestion();
                    }
                  }}
                >
                  Continue
                </button>
              </>
            )}
          </div>
        </div>
      )}



    {/* ✅ LIMIT POPUP — overlay on top of test */}
    {showLimitPopup && (
      <div className="limit-overlay">
        <div className="limit-card">
          <h2>{limitMessage?.title || ""}</h2>
          <p>{limitMessage?.description || ""}</p>

          {limitMessage?.isMilestone && limitMessage?.data?.milestoneMessage && (
            <p>{limitMessage.data.milestoneMessage}</p>
          )}

          {!limitMessage?.isMilestone && limitMessage?.footer && (
            <p>{limitMessage.footer}</p>
          )}

          <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "20px" }}>
            <button className="gotitbtn" onClick={handleFinishTest}>
              {limitMessage?.isMilestone ? "Finish Here" : "Got it"}
            </button>

            {limitMessage?.isMilestone && (
              <button
                className="gotitbtn"
                disabled={popupLoading}
                onClick={() => handleNextQuestion(true)}
              >
                {popupLoading ? "Loading..." : "Continue Challenge"}
              </button>
            )}
          </div>
        </div>
      </div>
    )}

    </div>
  );
}